/**
 * EventList Component
 *
 * Displays a virtualized list of events grouped by type.
 * Performance optimizations:
 * - react-window List for virtualization (only renders visible items)
 * - React.memo for row components
 * - Memoized grouping and flattening with useMemo
 * - Memoized click handlers with useCallback
 */

import { useMemo, useCallback, memo, CSSProperties, ReactElement } from 'react';
import { List } from 'react-window';
import { UnifiedEvent, EVENT_TYPE_INFO, EventType, formatRelativeTime } from '../types/events';
import { FireMarkerIcon, FireGroupHeaderIcon } from './icons';
import { EmptyState } from './EmptyState';

/**
 * Check if event was updated recently (within last 15 minutes).
 */
function isRecentlyUpdated(observedAt: string): boolean {
  const eventTime = new Date(observedAt).getTime();
  const now = Date.now();
  const fifteenMinutes = 15 * 60 * 1000;
  return (now - eventTime) < fifteenMinutes;
}

/**
 * Get severity color for fire incidents.
 */
function getSeverityBorderColor(severity: string | undefined): string {
  switch (severity) {
    case 'EMERGENCY':
      return '#dc2626'; // Red
    case 'WATCH_AND_ACT':
      return '#f97316'; // Orange
    case 'ADVICE':
      return '#10b981'; // Green
    case 'NOT_APPLICABLE':
      return '#6b7280'; // Gray
    default:
      return '#9ca3af'; // Light gray
  }
}

/**
 * Get status indicator color and icon.
 */
function getStatusIndicator(status: string | undefined): { color: string; icon: string } {
  switch (status) {
    case 'OUT_OF_CONTROL':
      return { color: '#dc2626', icon: '●' }; // Red dot
    case 'BEING_CONTROLLED':
      return { color: '#f97316', icon: '●' }; // Orange dot
    case 'UNDER_CONTROL':
      return { color: '#10b981', icon: '●' }; // Green dot
    case 'PATROL':
      return { color: '#6b7280', icon: '○' }; // Gray circle
    default:
      return { color: '#9ca3af', icon: '●' }; // Light gray
  }
}

interface EventListProps {
  events: UnifiedEvent[];
  onEventClick: (event: UnifiedEvent) => void;
}

/**
 * Row type for the flattened list - either a group header or an event item.
 */
type ListRow =
  | { type: 'header'; eventType: EventType; count: number }
  | { type: 'event'; event: UnifiedEvent };

/**
 * Group events by their type.
 */
function groupEventsByType(events: UnifiedEvent[]): Map<EventType, UnifiedEvent[]> {
  const groups = new Map<EventType, UnifiedEvent[]>();

  for (const event of events) {
    const existing = groups.get(event.type) || [];
    existing.push(event);
    groups.set(event.type, existing);
  }

  return groups;
}

/**
 * Priority order for event types (higher = more important, shown first).
 */
const EVENT_TYPE_PRIORITY: Record<EventType, number> = {
  FIRE_INCIDENT: 100,
  BURN_AREA: 95, // Burn areas shown just after fire incidents
  HEALTH: 90, // Health advisories shown prominently (often smoke/air quality related)
  AIRCRAFT: 80,
  WEATHER: 70,
  HOTSPOT: 60,
  TRANSPORT: 50,
  POWER_OUTAGE: 40,
  COMMUNITY_REPORT: 30,
};

/**
 * Fixed heights for list items.
 */
const HEADER_HEIGHT = 40;
// Fire incidents. 112 (was 100) so the title + alert badge, status/type/size
// pills, the wrapping location·time·distance line and the optional
// "More Details" link don't clip on narrow (~360px) phones where that content
// takes an extra line. The list is variable-size, so this only affects fire
// rows — non-fire rows stay compact.
const EVENT_ITEM_HEIGHT = 112;
const SIMPLE_EVENT_HEIGHT = 72; // Non-fire events

/**
 * Memoized Event Item Component (inline version for virtualized list)
 *
 * UI/UX Design Principles:
 * - Large, visible icons (32px fire icon)
 * - High contrast text (#1f2937 for primary, #4b5563 for secondary)
 * - Status shown as colored pill badges for scannability
 * - Distance always shown with "km away" for context
 * - Compact vertical spacing to reduce wasted space
 * - Clear visual hierarchy: Title > Status > Details > Time
 */
const EventItemContent = memo(function EventItemContent({
  event,
  onEventClick,
}: {
  event: UnifiedEvent;
  onEventClick: (event: UnifiedEvent) => void;
}) {
  const info = EVENT_TYPE_INFO[event.type];
  const isFireIncident = event.type === 'FIRE_INCIDENT';
  const severity = event.data?.severity as 'EMERGENCY' | 'WATCH_AND_ACT' | 'ADVICE' | 'NOT_APPLICABLE' | undefined;
  const status = event.data?.status as string | undefined;
  const statusIndicator = getStatusIndicator(status);
  const isRecent = isRecentlyUpdated(event.observedAt);

  // Extract fire incident specific data with proper typing
  const alertLevel = event.data?.alertLevel as string | undefined;
  const fireType = event.data?.fireType as string | undefined;
  const sizeHectares = event.data?.sizeHectares as number | undefined;
  const councilArea = event.data?.councilArea as string | undefined;
  const vicLocation = event.data?.location as string | undefined; // VIC fires store location here
  const moreInfoUrl = event.data?.moreInfoUrl as string | undefined;

  // Location fallback: NSW uses councilArea, VIC uses data.location or description
  // VIC locations can be very long lists - truncate to first 2-3 areas
  const rawLocation = councilArea || vicLocation || event.description;
  const displayLocation = rawLocation && rawLocation.length > 60
    ? rawLocation.split(',').slice(0, 3).join(',') + '...'
    : rawLocation;

  const handleClick = useCallback(() => {
    onEventClick(event);
  }, [event, onEventClick]);

  // Determine border color based on event type and severity
  const borderColor = isFireIncident
    ? getSeverityBorderColor(severity)
    : info.color;

  return (
    <div
      className="event-item"
      style={{
        borderLeft: `4px solid ${borderColor}`,
        borderBottom: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      <button
        className="event-item-button"
        onClick={handleClick}
        style={{
          padding: '10px 12px 10px 10px',
          alignItems: 'flex-start',
        }}
      >
        {/* Icon - larger and properly aligned */}
        <div style={{
          flexShrink: 0,
          marginRight: '12px',
          marginTop: '2px',
          width: '32px',
          display: 'flex',
          justifyContent: 'center',
        }}>
          {isFireIncident ? (
            <FireMarkerIcon
              severity={severity || 'ADVICE'}
              isPlannedBurn={event.data?.isPlannedBurn === true}
              size="medium"
              className="event-item-icon"
              style={{
                transform: 'scale(0.8)', // 32px effective size
              }}
            />
          ) : (
            <span
              style={{
                backgroundColor: info.color,
                borderRadius: '50%',
                width: '28px',
                height: '28px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
              }}
            >
              {info.icon}
            </span>
          )}
        </div>

        {/* Content area */}
        <div className="event-item-content" style={{ flex: 1, minWidth: 0 }}>
          {/* Row 1: Title + Alert Badge */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '4px',
          }}>
            <span
              className="event-item-title"
              style={{
                color: '#f3f4f6', // High contrast white
                fontWeight: '600',
                fontSize: '13px',
                lineHeight: '1.3',
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {event.title}
            </span>

            {/* Alert level badge */}
            {isFireIncident && alertLevel ? (
              <span style={{
                backgroundColor: severity === 'EMERGENCY' ? '#dc2626' :
                                severity === 'WATCH_AND_ACT' ? '#f97316' :
                                severity === 'NOT_APPLICABLE' ? '#6b7280' : '#10b981',
                color: 'white',
                padding: '3px 8px',
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: '700',
                whiteSpace: 'nowrap',
                textTransform: 'uppercase',
                letterSpacing: '0.025em',
              }}>
                {alertLevel}
              </span>
            ) : null}
          </div>

          {/* Row 2: Status badge + Fire type + Size */}
          {isFireIncident ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '4px',
              flexWrap: 'wrap',
            }}>
              {/* Status as colored pill badge - much more visible than dots */}
              {status ? (
                <span style={{
                  backgroundColor: statusIndicator.color,
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '10px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em',
                }}>
                  {status.replace(/_/g, ' ')}
                </span>
              ) : null}

              {/* Fire type and size in readable text */}
              {(fireType || (sizeHectares && sizeHectares > 0)) ? (
                <span style={{
                  color: '#d1d5db',
                  fontSize: '12px',
                }}>
                  {fireType || ''}
                  {sizeHectares && sizeHectares > 0 ? (
                    <>{fireType ? ' • ' : ''}{sizeHectares.toLocaleString()} ha</>
                  ) : null}
                </span>
              ) : null}
            </div>
          ) : null}

          {/* Row 3: Location + Time + Distance */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: '#d1d5db', // Higher contrast secondary text
            fontSize: '12px',
            flexWrap: 'wrap',
          }}>
            {/* Location - NSW uses councilArea, VIC uses data.location or description */}
            {isFireIncident && displayLocation ? (
              <>
                <span style={{ color: '#e5e7eb' }}>📍 {displayLocation}</span>
                <span style={{ opacity: 0.5 }}>•</span>
              </>
            ) : null}

            {/* Time */}
            <span style={{ color: '#e5e7eb' }}>{formatRelativeTime(event.observedAt)}</span>

            {/* Distance - always with "away" for context */}
            {event._distanceKm !== undefined && (
              <>
                <span style={{ opacity: 0.5 }}>•</span>
                <span style={{
                  color: event._distanceKm < 10 ? '#fbbf24' : '#e5e7eb',
                  fontWeight: event._distanceKm < 10 ? '600' : '500',
                }}>
                  {event._distanceKm.toFixed(1)} km away
                </span>
              </>
            )}

            {/* Recently updated indicator */}
            {isRecent && (
              <>
                <span style={{ opacity: 0.5 }}>•</span>
                <span style={{
                  color: '#60a5fa',
                  fontWeight: '600',
                }}>
                  UPDATED
                </span>
              </>
            )}
          </div>

          {/* More Info Link - inline, subtle */}
          {isFireIncident && moreInfoUrl ? (
            <a
              href={moreInfoUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                display: 'inline-block',
                marginTop: '6px',
                color: '#f87171',
                textDecoration: 'none',
                fontSize: '11px',
                fontWeight: '500',
              }}
            >
              More Details →
            </a>
          ) : null}
        </div>

        {/* Arrow indicator */}
        <span
          className="event-item-arrow"
          style={{
            color: '#6b7280',
            marginLeft: '8px',
            fontSize: '14px',
          }}
        >
          ›
        </span>
      </button>
    </div>
  );
});

/**
 * Memoized Group Header Component
 */
const GroupHeaderContent = memo(function GroupHeaderContent({ type, count }: { type: EventType; count: number }) {
  const info = EVENT_TYPE_INFO[type];

  return (
    <div
      className="event-group-header"
      style={{
        borderLeftColor: info.color,
        padding: '8px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
      }}
    >
      {type === 'FIRE_INCIDENT' ? (
        <FireGroupHeaderIcon
          className="event-group-icon"
          style={{ width: '24px', height: '24px' }}
        />
      ) : (
        <span className="event-group-icon" style={{ fontSize: '18px' }}>{info.icon}</span>
      )}
      <span
        className="event-group-label"
        style={{
          fontWeight: '600',
          fontSize: '14px',
          color: '#f3f4f6',
        }}
      >
        {info.label}
      </span>
      <span
        className="event-group-count"
        style={{
          backgroundColor: info.color,
          color: 'white',
          padding: '2px 8px',
          borderRadius: '10px',
          fontSize: '12px',
          fontWeight: '600',
          marginLeft: 'auto',
        }}
      >
        {count}
      </span>
    </div>
  );
});

/**
 * Row props for the virtualized list
 */
interface RowProps {
  rows: ListRow[];
  onEventClick: (event: UnifiedEvent) => void;
}

/**
 * Row component for the virtualized list
 */
function Row({
  index,
  style,
  rows,
  onEventClick,
}: {
  ariaAttributes: { "aria-posinset": number; "aria-setsize": number; role: "listitem" };
  index: number;
  style: CSSProperties;
} & RowProps): ReactElement {
  const row = rows[index];

  if (row.type === 'header') {
    return (
      <div style={style}>
        <GroupHeaderContent type={row.eventType} count={row.count} />
      </div>
    );
  }

  return (
    <div style={style}>
      <EventItemContent event={row.event} onEventClick={onEventClick} />
    </div>
  );
}

/**
 * Main EventList Component with virtualization
 */
const EventList = memo(function EventList({ events, onEventClick }: EventListProps) {
  // Flatten events into a mixed list of headers and items for virtualization
  const { flatRows, rowHeightFn } = useMemo(() => {
    const groups = groupEventsByType(events);

    // Sort groups by priority
    const sortedGroups = Array.from(groups.entries()).sort(
      ([typeA], [typeB]) =>
        EVENT_TYPE_PRIORITY[typeB] - EVENT_TYPE_PRIORITY[typeA]
    );

    // Flatten into rows (headers + events)
    const rows: ListRow[] = [];
    for (const [eventType, typeEvents] of sortedGroups) {
      // Add header row
      rows.push({ type: 'header', eventType, count: typeEvents.length });
      // Add event rows
      for (const event of typeEvents) {
        rows.push({ type: 'event', event });
      }
    }

    // Row height function based on row type
    const heightFn = (index: number): number => {
      const row = rows[index];
      if (row.type === 'header') {
        return HEADER_HEIGHT;
      }
      // Fire incidents are taller due to more info
      return row.event.type === 'FIRE_INCIDENT' ? EVENT_ITEM_HEIGHT : SIMPLE_EVENT_HEIGHT;
    };

    return { flatRows: rows, rowHeightFn: heightFn };
  }, [events]);

  // Memoize click handler
  const handleEventClick = useCallback((event: UnifiedEvent) => {
    onEventClick(event);
  }, [onEventClick]);

  if (events.length === 0) {
    return (
      <EmptyState
        icon="🔥"
        title="No events found"
        hint="No fires in this area and time window. Pan the map or widen your search to see more."
      />
    );
  }

  // For small lists, use non-virtualized rendering for simplicity
  if (flatRows.length <= 20) {
    return (
      <div className="event-list">
        {flatRows.map((row) => (
          row.type === 'header' ? (
            <GroupHeaderContent key={`header-${row.eventType}`} type={row.eventType} count={row.count} />
          ) : (
            <EventItemContent key={row.event.id} event={row.event} onEventClick={handleEventClick} />
          )
        ))}
      </div>
    );
  }

  // Use virtualized list for large datasets
  return (
    <div className="event-list" style={{ height: '100%', overflow: 'hidden' }}>
      <List
        rowCount={flatRows.length}
        rowHeight={rowHeightFn}
        rowComponent={Row}
        rowProps={{ rows: flatRows, onEventClick: handleEventClick }}
        overscanCount={5}
        style={{ height: '100%', width: '100%' }}
      />
    </div>
  );
});

export default EventList;
